import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import PageLayout from '../../components/shared/layout/PageLayout';
import CMSConsumerCarouselBanner from '../../section/CMS/components/banner/CMSConsumerCarouselBanner';
import CompanyGrid from '../../components/shared/grid/CompanyGrid';
import TextHeader from '../../components/textHeader/TextHeader';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import MissingPage from '../../components/shared/errorMessages/MissingPage';
import { captureAnalytics } from '../../utilities/Analytics';
import { getCategoryPageData } from '../../section/CMS/api/cmsApi';
import { CMSPageData } from '../../section/CMS/types/CMSTypes';

// Helper function to determine file server URL based on environment
const getFileServerUrl = (): string => {
  const currentDomain = window.location.hostname;
  if (currentDomain.includes('staging')) {
    return 'https://file-server.hertex-staging.net';
  }
  return 'https://file-server.hertex.co.za';
};

// Helper function to construct image URL
const constructImageUrl = (filename: string): string => {
  if (!filename) return '';
  const fileServerUrl = getFileServerUrl();
  const cleanUrl = fileServerUrl.replace('/api', '').replace(/\/$/, '');
  return `${cleanUrl}/media/large/${filename}`;
};

// Helper function to construct video URL
const constructVideoUrl = (filename: string): string => {
  if (!filename) return '';
  const fileServerUrl = getFileServerUrl();
  const cleanUrl = fileServerUrl.replace('/api', '').replace(/\/$/, '');
  return `${cleanUrl}/media/video/${filename}`;
};

// Helper function to transform URLs
const transformUrl = (url: string): string => {
  if (!url) return '';
  return url.replace('https://trade.hertex.co.za/', 'https://hertex.co.za/');
};

const AboutUsPage = () => {
  const location = useLocation();

  // Fetch CMS data for About Us
  const {
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse
  } = useQuery(
    ['cms-about-us'],
    () => getCategoryPageData('about us', 'live', 'about_us'),
    {
      staleTime: 600000, // Cache data for 10 minutes
    }
  );

  // Get the CMS data
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    captureAnalytics('pageview', location.pathname, 'About Us');
  }, [location.pathname]);

  if (cmsLoading) {
    return <LoadingScreen />;
  }

  if (cmsError || !cmsData) {
    return <MissingPage />;
  }

  return (
    <>
      {/* CMS Banner */}
      <CMSConsumerCarouselBanner
        bannerMediaOption={cmsData.banner_media_option || 'carousel'}
        bannerMediaCarousel={cmsData.banner_media_carousel || []}
        bannerMediaVideo={cmsData.banner_media_video || null}
        bannerMediaVideoLink={cmsData.banner_media_video_link || ''}
        bannerLinkCategoryId={cmsData.banner_link_category_id || null}
        bannerWidth={cmsData.banner_width || 2880}
        bannerHeight={cmsData.banner_height || 1050}
      />

      {/* Page Title and Description */}
      <TextHeader
        heading={cmsData.page_title || 'About Us'}
        content={cmsData.description || ''}
        subContent=''
        isCentered={false}
        width='70%'
        marginTop={10}
        marginBottom={5}
      />

      {/* Section One */}
      {(cmsData.section_one_media_file || cmsData.section_one_description) && (
        <CompanyGrid
          marginTop={15}
          marginBottom={15}
          image={cmsData.section_one_media_file ? constructImageUrl(cmsData.section_one_media_file) : ''}
          hasImage={!!cmsData.section_one_media_file}
          tinyLogo={cmsData.section_one_logo_media_file ? constructImageUrl(cmsData.section_one_logo_media_file) : ''}
          hasButton={!!(cmsData.section_one_link_url || cmsData.section_one_web_link)}
          link={transformUrl(cmsData.section_one_link_url || cmsData.section_one_web_link || '')}
          buttonTitle={cmsData.section_one_link_button_text || 'Learn More'}
          paragraph1={cmsData.section_one_description || ''}
          isReverse={cmsData.section_one_media_alignment === 'right'}
          heading={cmsData.section_one_title || ''}
          hasExternalLink={!!(cmsData.section_one_link_url || cmsData.section_one_web_link)}
          externalLink={transformUrl(cmsData.section_one_link_url || cmsData.section_one_web_link || '')}
        />
      )}

      {/* Section Two */}
      {(cmsData.section_two_media_file || cmsData.section_two_description) && (
        <CompanyGrid
          marginTop={15}
          marginBottom={15}
          image={cmsData.section_two_media_file ? constructImageUrl(cmsData.section_two_media_file) : ''}
          hasImage={!!cmsData.section_two_media_file}
          tinyLogo={cmsData.section_two_logo_media_file ? constructImageUrl(cmsData.section_two_logo_media_file) : ''}
          hasButton={!!(cmsData.section_two_link_url || cmsData.section_two_web_link)}
          link={transformUrl(cmsData.section_two_link_url || cmsData.section_two_web_link || '')}
          buttonTitle={cmsData.section_two_link_button_text || 'Learn More'}
          paragraph1={cmsData.section_two_description || ''}
          isReverse={cmsData.section_two_media_alignment === 'right'}
          heading={cmsData.section_two_title || ''}
          hasExternalLink={!!(cmsData.section_two_link_url || cmsData.section_two_web_link)}
          externalLink={transformUrl(cmsData.section_two_link_url || cmsData.section_two_web_link || '')}
        />
      )}

      {/* Section Three */}
      {(cmsData.section_three_media_file || cmsData.section_three_description) && (
        <CompanyGrid
          marginTop={15}
          marginBottom={15}
          image={cmsData.section_three_media_file ? constructImageUrl(cmsData.section_three_media_file) : ''}
          hasImage={!!cmsData.section_three_media_file}
          tinyLogo={cmsData.section_three_logo_media_file ? constructImageUrl(cmsData.section_three_logo_media_file) : ''}
          hasButton={!!(cmsData.section_three_link_url || cmsData.section_three_web_link)}
          link={transformUrl(cmsData.section_three_link_url || cmsData.section_three_web_link || '')}
          buttonTitle={cmsData.section_three_link_button_text || 'Learn More'}
          paragraph1={cmsData.section_three_description || ''}
          isReverse={cmsData.section_three_media_alignment === 'right'}
          heading={cmsData.section_three_title || ''}
          hasExternalLink={!!(cmsData.section_three_link_url || cmsData.section_three_web_link)}
          externalLink={transformUrl(cmsData.section_three_link_url || cmsData.section_three_web_link || '')}
        />
      )}
    </>
  );
};

export default AboutUsPage;